import { checkPasswordStrength, MIN_LENGTH, type StrengthLabel } from '@/lib/password-strength'

// =============================================================================
// PasswordStrengthMeter
//
// 4-segment horizontal bar that fills + colors as the password gets
// stronger. Hidden when the field is empty so the form doesn't start
// life with a "too-short" red bar before the user has typed anything.
// =============================================================================

const LABEL_COPY: Record<StrengthLabel, string> = {
  'too-short': `At least ${MIN_LENGTH} characters required`,
  weak: 'Weak — add length and variety',
  fair: 'Fair',
  good: 'Good',
  strong: 'Strong',
}

const LABEL_COLOR: Record<StrengthLabel, string> = {
  'too-short': 'text-[var(--color-signal)]',
  weak: 'text-[var(--color-signal)]',
  fair: 'text-[var(--color-muted)]',
  good: 'text-[var(--color-accent)]',
  strong: 'text-[var(--color-accent)]',
}

interface Props {
  password: string
}

export function PasswordStrengthMeter({ password }: Props) {
  if (password.length === 0) return null
  const { score, label } = checkPasswordStrength(password)
  // Segment fill: score 1..4 lights up 1..4 segments. score 0
  // (too-short) leaves the bar empty but colors the first segment red
  // as a hint that something's wrong.
  const filled = score === 0 ? 1 : score
  return (
    <div className="flex flex-col gap-1 mt-1">
      <div className="flex gap-1" aria-hidden>
        {[1, 2, 3, 4].map((i) => {
          const isFilled = i <= filled
          const colorClass = !isFilled
            ? 'bg-[var(--color-border)]'
            : score <= 1
              ? 'bg-[var(--color-signal)]'
              : score === 2
                ? 'bg-[var(--color-signal)]/60'
                : score === 3
                  ? 'bg-[var(--color-accent)]/70'
                  : 'bg-[var(--color-accent)]'
          return (
            <span
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${colorClass}`}
            />
          )
        })}
      </div>
      <span className={`text-[11px] ${LABEL_COLOR[label]}`}>{LABEL_COPY[label]}</span>
    </div>
  )
}
